import {
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentWorkflow,
  InlineEditItem,
  LockType,
  WorkflowEvent,
  WorkflowEventTypes,
  WorkflowState,
  WorkingCopyKey,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import PQueue from 'p-queue';
import type { APGLEntityEntry } from '@amzn/awscat-editor/dist/extensions/apgl-entity';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { promiseTimeout } from '../api/UtilsApi';
import FlashbarMessageBroker from '../common/FlashbarMessageBroker';
import { log } from '../loggers/Logger';
import {
  LoadedDocument,
  draftToLoadedDocument,
  finalizingToLoadedDocument,
  publicationToLoadedDocument,
} from '../model/LoadedDocument';
import { ContentSchemaSection } from '../model/ContentSchemaSection';
import { deepCloneContent } from '../model/CloneContent';
import { isPublicationViewable } from '../model/WorkflowViewability';
import { AdhocMigration, AsyncAdhocMigration, SyncAdhocMigration } from './AdhocMigration';
import { AutoSavingService, AutoSaveState } from './AutoSavingService';
import { PendingFlushCoordinator } from './PendingFlushCoordinator';
import {
  ContentValidationError,
  ContentValidationIssue,
  SaveDraftError,
  ServiceCallError,
  UserNotLoggedInError,
} from './Errors';
import {
  filterInlineEdits as filterInlineEditsImpl,
  getNodeIdMappings,
  InlineTextFilter,
  NodeIdMappings,
} from './InlineTextOperations';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';
import { UserService } from './UserService';

// The backend applies a LOCK workflow event asynchronously, so the lock is not
// visible the instant postWorkflowEvent resolves. Poll the workflow at this
// cadence until the lock shows up, giving up once this much time has elapsed.
const LOCK_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const LOCK_POLL_INTERVAL_MS = 2000;

// Shared so `coAuthors` returns the same reference while no document is open;
// `useSyncExternalStore` snapshots must be stable between changes.
const NO_CO_AUTHORS: readonly ContentAccessUser[] = Object.freeze([]);

/** True when `options` permits mutation (edit-draft or edit-finalizing). */
/** Builds the message shown when a lock held by the system or another user blocks a write. */
function describeLockHeldByOther(lockType: LockType | undefined): string {
  if (lockType === LockType.SYSTEM) {
    return 'Content is locked by system. Please wait until the scan finishes and retry.';
  }
  return 'Content is locked by another user. Please wait until they release the lock and retry.';
}

function isEditable(options: ContentLoadOptions): boolean {
  return options.kind === 'edit-draft' || options.kind === 'edit-finalizing';
}

/**
 * Builds a `.catch` handler that rethrows a `ServiceCallError` as a `SaveDraftError` and rethrows anything else.
 */
function handleMplClientError(message: string): (err: unknown) => never {
  return (err: unknown) => {
    if (!(err instanceof ServiceCallError)) throw err;
    throw SaveDraftError.fromServiceCall(err, message);
  };
}

/** Picks the working-copy slot (draft vs finalizing) for an editable open. */
function workingCopyKeyFor(options: ContentLoadOptions): WorkingCopyKey {
  if (options.kind === 'edit-draft') return WorkingCopyKey.contentworkingcopy;
  if (options.kind === 'edit-finalizing') return WorkingCopyKey.finalizingworkingcopy;
  throw new Error(`workingCopyKeyFor called with non-editable kind: ${options.kind}`);
}

/** Viewing a draft. Used by the author, co-authors, admins, peer reviewers, and bar raisers. */
export interface ReadDraftOptions {
  readonly kind: 'read-draft';
}

/** Editing a draft. Used by the author, co-authors, and admins. */
export interface EditDraftOptions {
  readonly kind: 'edit-draft';
}

/** Viewing a published version. Omit `workflowId` for the latest; specify it for a historical version. */
export interface ReadPublicationOptions {
  readonly kind: 'read-publication';
  readonly workflowId?: number;
}

/** Previewing a tech writer's finalizing workspace. Omit `userId` for the caller's own; specify it to view another writer's. */
export interface ReadFinalizingOptions {
  readonly kind: 'read-finalizing';
  readonly workflowId: number;
  readonly userId?: string;
}

/** Tech writer editing their own finalizing workspace. */
export interface EditFinalizingOptions {
  readonly kind: 'edit-finalizing';
  readonly workflowId: number;
}

/** How a content should be loaded. */
export type ContentLoadOptions =
  | ReadDraftOptions
  | EditDraftOptions
  | ReadPublicationOptions
  | ReadFinalizingOptions
  | EditFinalizingOptions;

/** A workflow assignee resolved from their user ID when profile data is available. */
export interface ContentAttributionUser {
  readonly userId: string;
  readonly userName?: string;
}

/** Workflow-role attribution associated with the open content. */
export interface ContentAttributions {
  readonly contentBarRaiser?: ContentAttributionUser;
  readonly technicalWriter?: ContentAttributionUser;
}

/** Handle to the currently-open loaded document, plus how it was opened. */
export interface LoadedDocumentHandle {
  readonly contentId: string;
  readonly options: ContentLoadOptions;
  /** Server-side document, without any local edits. */
  readonly document: LoadedDocument;
  /** Undefined until `openEvaluationScore()` is called, or when the content has no quality evaluation yet. */
  readonly evaluationScore: number | undefined;
  /**
   * Workflow events for the current version. For a draft whose publications
   * were unpublished, also includes those publications' events when the user
   * may see their activity. Undefined until loaded.
   */
  readonly events: readonly WorkflowEvent[] | undefined;
  /** True when `events` includes an unpublished publication's events. Undefined until loaded. */
  readonly hasUnpublishedEvents?: boolean;
  /** Review and workflow-role attribution. Undefined until its background load completes. */
  readonly attributions?: ContentAttributions;
}

export type ContentEditorEvents = {
  /** Fires when any observable state changes. Use for `useSyncExternalStore`. */
  changed: () => void;
};

/** Outcome of validating content: per-section error maps, link issues, and overall validity. */
export interface ContentValidationOutcome {
  readonly contentMetadataError: ReadonlyMap<string, string>;
  readonly templateValidationError: ReadonlyMap<string, string>;
  readonly contentDataLinkValidationError: readonly ContentValidationIssue[];
  readonly isValid: boolean;
}

/**
 * Validates content sections and normalizes its links in place. Implemented by
 * the legacy `validateContent`, injected into the service — see constructor.
 */
export type ContentValidator = (
  content: Content,
  sections: Set<ContentSchemaSection>,
  entities: Record<string, APGLEntityEntry>,
) => ContentValidationOutcome;

/**
 * Opens a single content (draft, publication, or finalizing workspace)
 * for viewing or editing. Use for any page that shows or edits the body
 * of one content item. Lifecycle: `open` → `stageEdit`* → `save` /
 * `closeAndSave` / `closeAndDiscard`.
 */
export class ContentEditorService {
  private readonly _autoSaving: AutoSavingService;
  private readonly _flushCoordinator: PendingFlushCoordinator;
  private readonly _emitter = new TypedEmitter<ContentEditorEvents>();
  private readonly _migrations: readonly AdhocMigration[];
  private readonly _mpl: IMplServiceClient;
  private readonly _queue = new PQueue({ concurrency: 1 });
  private readonly _userService: UserService;
  private readonly _contentValidator: ContentValidator;

  private _nodeIdMappings: NodeIdMappings | undefined = undefined;
  private _opened: LoadedDocumentHandle | undefined = undefined;
  private _unsubscribeAutoSaving: Unsubscribe | undefined = undefined;
  private _attributionsRequestId = 0;
  private _validationEntities: Record<string, APGLEntityEntry> = {};

  constructor(
    mpl: IMplServiceClient,
    autoSaving: AutoSavingService,
    flushCoordinator: PendingFlushCoordinator,
    userService: UserService,
    // Injected rather than imported: the validator comes from legacy code that
    // pulls in Cloudscape, which the unit-test ESM resolver can't load. Keeping
    // the import out of this module lets the many tests that mock the service
    // load without tripping that dependency. Clean this up when the legacy
    // validator no longer drags in Cloudscape.
    contentValidator: ContentValidator,
    migrations: readonly AdhocMigration[] = [],
  ) {
    this._mpl = mpl;
    this._autoSaving = autoSaving;
    this._flushCoordinator = flushCoordinator;
    this._userService = userService;
    this._contentValidator = contentValidator;
    this._migrations = migrations;
  }

  /**
   * Set the entity dictionary used to resolve @-mentions during save-time
   * validation. Kept current from the React layer, which is the only place
   * entities are available.
   */
  setValidationEntities(entities: Record<string, APGLEntityEntry>): void {
    this._validationEntities = entities;
  }

  getValidationEntities(): Readonly<Record<string, Readonly<APGLEntityEntry>>> {
    return this._validationEntities;
  }

  /**
   * Raw server snapshot of the loaded document — does NOT include staged or auto-saved edits.
   * Use {@link content} to render the latest content in the UI.
   */
  get loadedDocumentHandle(): LoadedDocumentHandle | undefined {
    return this._opened;
  }

  /** True when the loaded document is open for editing. */
  get isEditable(): boolean {
    return this._opened !== undefined && isEditable(this._opened.options);
  }

  /** True when there are unsaved local edits. */
  get isDirty(): boolean {
    return this._autoSaving.pendingEdits?.isDirty ?? false;
  }

  get autoSaveState(): AutoSaveState {
    return this._autoSaving.pendingEdits?.saveState ?? 'idle';
  }

  /** Current node ID mappings derived from the loaded content, or undefined if nothing is open. */
  get nodeIdMappings(): NodeIdMappings | undefined {
    return this._nodeIdMappings;
  }

  /**
   * Filters inline edit items to only those belonging to a specified content region.
   * Re-invoke when this service emits `changed` — content edits may alter node ID mappings.
   */
  filterInlineEdits(
    items: readonly InlineEditItem[],
    filter: InlineTextFilter,
  ): readonly InlineEditItem[] {
    if (!this._nodeIdMappings) return [];
    return filterInlineEditsImpl(filter, this._nodeIdMappings, items);
  }

  /**
   * Content to render: staged edit if any, otherwise the server snapshot. May
   * miss the user's latest typing that editors haven't staged yet; use
   * {@link pullContent} before building an edit on top of it.
   */
  get content(): Content | undefined {
    return this._autoSaving.pendingEdits?.content ?? this._opened?.document?.content;
  }

  /**
   * Returns the content after asking every open editor to stage its pending
   * typing first, so an edit built on the result loses none of the user's work.
   */
  pullContent(): Content | undefined {
    this._flushCoordinator.drainFlushers();
    return this.content;
  }

  /** Audience to render: staged edit if any, otherwise the server snapshot. */
  get audience(): ContentAudience | undefined {
    return this._autoSaving.pendingEdits?.audience ?? this._opened?.document?.contentAudience;
  }

  /** Co-authors for the currently-open document. */
  get coAuthors(): readonly ContentAccessUser[] {
    return this._opened?.document.coAuthors ?? NO_CO_AUTHORS;
  }

  /**
   * Open `contentId` for viewing or editing. Throws if another content is already open.
   *
   * @throws {ContentAlreadyOpenError} when another content is already open.
   * @throws {ServiceCallError} when the backend call fails.
   */
  async open(contentId: string, options: ContentLoadOptions): Promise<void> {
    await this._queue.add(() => this.doOpen(contentId, options));
  }

  /**
   * Fetch the quality evaluation score for the open content.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async openEvaluationScore(): Promise<void> {
    await this._queue.add(() => this.doOpenEvaluationScore());
  }

  /**
   * Stage a content edit. Auto-flushed to the working copy after 3s of
   * idle time; promoted to the real content on {@link save}. Safe to
   * call on every keystroke.
   */
  stageEdit(content: Content, audience?: ContentAudience): void {
    const current = this.requireOpen('stageEdit');
    this.requireEditable(current.options, 'stageEdit');
    this.recalculateNodeIdMappings(content);
    // @ts-expect-error TS2345 - strict migration
    this._autoSaving.stageContentEdit(content, audience ?? this.audience);
  }

  /**
   * Add a co-author. Takes effect immediately — calls the server and
   * updates the in-memory handle on success. Co-authors do not go
   * through the stageEdit / auto-saving system.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async addCoAuthor(user: ContentAccessUser): Promise<void> {
    await this._queue.add(() =>
      this.mutateCoAuthors(
        'addCoAuthor',
        (contentId) => this._mpl.shareContents([contentId], [user.userId]),
        (coAuthors) => [...coAuthors, user],
      ),
    );
  }

  /**
   * Remove a co-author. Takes effect immediately — calls the server and
   * updates the in-memory handle on success. Co-authors do not go
   * through the stageEdit / auto-saving system.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async removeCoAuthor(userId: string): Promise<void> {
    await this._queue.add(() =>
      this.mutateCoAuthors(
        'removeCoAuthor',
        (contentId) => this._mpl.unShareContents([contentId], [userId]),
        (coAuthors) => coAuthors.filter((u) => u.userId !== userId),
      ),
    );
  }

  /**
   * Immediately save the pending staged edit into the working copy.
   * Use after {@link stageEdit} when the edit should be persisted right
   * away instead of waiting for the 3-second auto-flush timer.
   *
   * @throws {SaveWorkingCopyError} when the working-copy write fails.
   */
  async flush(): Promise<void> {
    await this._queue.add(async () => {
      this.requireOpen('flush');
      await this._autoSaving.flush();
    });
  }

  /**
   * Persist staged edits and reload the document. Leaves the content open.
   *
   * @throws {ContentValidationError} when a draft save fails pre-save validation.
   * @throws {SaveWorkingCopyError} when the intermediate working-copy flush fails.
   * @throws {SaveDraftError} when committing to the server or recycling the
   *   working copy afterwards fails.
   */
  async save(opts: { readonly isUnlock?: boolean } = {}): Promise<void> {
    await this._queue.add(() => this.doSave(opts));
  }

  /**
   * Save, then close.
   *
   * @throws {ContentValidationError} when a draft save fails pre-save validation.
   * @throws {SaveWorkingCopyError} when the intermediate working-copy flush fails.
   * @throws {SaveDraftError} when committing to the server or recycling the
   *   working copy afterwards fails.
   */
  async closeAndSave(opts: { readonly isUnlock?: boolean } = {}): Promise<void> {
    await this._queue.add(() => this.doCloseAndSave(opts));
  }

  /**
   * Discard staged edits and close. No-op when nothing is open.
   *
   * Pass `{ isUnlock: true }` when the close is a deliberate exit from an edit
   * session (e.g. "Discard all changes") so the server-side edit lock acquired
   * on entering the editor is released for the next author. Omit it for
   * incidental closes (unmount cleanup, closing a read view) that must not
   * touch the lock.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async closeAndDiscard(opts: { readonly isUnlock?: boolean } = {}): Promise<void> {
    await this._queue.add(() => this.doCloseAndDiscard(opts));
  }

  /**
   * Take the edit lock for the current user when they don't hold it yet.
   * Call before a server write outside of save, such as an image or attachment
   * upload, because the server accepts those only from the lock holder.
   *
   * @throws {SaveDraftError} when the system or another user holds the lock,
   *   or when taking or confirming the lock fails.
   * @throws {UserNotLoggedInError}
   */
  async acquireLockIfNeeded(): Promise<void> {
    await this._queue.add(async () => {
      const current = this.requireOpen('acquireLockIfNeeded');
      this.requireEditable(current.options, 'acquireLockIfNeeded');
      await this.doAcquireLockIfNeeded(current);
    });
  }

  /**
   * Re-fetch the workflow from the server and update the loaded document handle.
   * Use after an external event (e.g., scan completion) transitions the workflow state.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async refreshWorkflow(): Promise<void> {
    await this._queue.add(() => this.doRefreshWorkflow());
  }

  /**
   * Re-fetch workflow events for the current version (plus any unpublished
   * publications' events, see {@link LoadedDocumentHandle.events}) and update the handle.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async refreshEvents(): Promise<void> {
    await this._queue.add(() => this.doRefreshEvents());
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof ContentEditorEvents>(event: K, listener: ContentEditorEvents[K]): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  /**
   * @throws {ContentAlreadyOpenError} when another content is already open.
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async doOpen(contentId: string, options: ContentLoadOptions): Promise<void> {
    if (this._opened) {
      throw new Error(
        `Cannot open content "${contentId}": content "${this._opened.contentId}" is already open. ` +
          `Call closeAndDiscard() or closeAndSave() first.`,
      );
    }

    // Step 1: Fetch the server-side document.
    const document = await this.fetchDocument(contentId, options);

    // Step 2: Open autosaving (editable only). Rehydrates the working copy
    // if one exists from a previous session (e.g. crashed tab).
    if (isEditable(options)) {
      // Subscribe before opening so we don't miss the initial 'changed'
      // emission from AutoSavingService.open. Edit-state fields are
      // live-read via getters, so we just re-emit here.
      this._unsubscribeAutoSaving = this._autoSaving.on('changed', () => {
        this._emitter.emit('changed');
      });
      try {
        await this._autoSaving.open(contentId, {
          workingCopyKey: workingCopyKeyFor(options),
          isRestore: true,
        });
      } catch (err) {
        // Don't leak the subscription if opening autosave fails.
        this._unsubscribeAutoSaving();
        this._unsubscribeAutoSaving = undefined;
        throw err;
      }
    }

    // Step 3: Publish the loaded document handle.
    this._opened = {
      contentId,
      options,
      document,
      evaluationScore: undefined,
      events: undefined,
    };

    // Step 4: Run sync migrations against the live content (rehydrated
    // working copy if any, otherwise the server snapshot).
    if (isEditable(options)) {
      const content = this.content;
      if (!content) throw new Error('it is expected that content should be set');
      const migrated = this.runSyncMigrations(content);
      this._opened = { ...this._opened!, document: { ...document, content: migrated } };
    }

    // Step 4.2: Calculate node id mappings, required to map inline comments
    // and edits to editor sections.
    this.recalculateNodeIdMappings(this.content ?? document.content);

    // Step 5: Notify subscribers and kick off async migrations.
    this._emitter.emit('changed');
    if (isEditable(options)) {
      this.scheduleAsyncMigrations(contentId);
    }

    // Step 6: Load workflow events in the background (non-blocking).
    this.scheduleEventsFetch();
    this.scheduleAttributionsFetch();
  }

  /** Apply sync migrations sequentially. Sync migrations must not throw. */
  private runSyncMigrations(content: Content): Content {
    return this._migrations
      .filter((m): m is SyncAdhocMigration => m.kind === 'sync')
      .reduce((result, migration) => migration.apply(result), content);
  }

  /** Fire-and-forget every async migration in parallel; failures are isolated. */
  private scheduleAsyncMigrations(expectedContentId: string): void {
    this._migrations
      .filter((m): m is AsyncAdhocMigration => m.kind === 'async')
      .forEach((m) => void this.runOneAsyncMigration(m, expectedContentId));
  }

  private async runOneAsyncMigration(
    migration: AsyncAdhocMigration,
    expectedContentId: string,
  ): Promise<void> {
    try {
      await migration.run(this);
    } catch (err) {
      log.error(
        { err, migrationName: migration.name, contentId: expectedContentId },
        'Async AdhocMigration failed',
      );
      FlashbarMessageBroker.create().pushErrorMessage(
        'There was a problem loading this pattern. Please reload the page and try again.',
        undefined,
        undefined,
        'Reload',
        () => window.location.reload(),
      );
    }
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async doOpenEvaluationScore(): Promise<void> {
    const current = this.requireOpen('openEvaluationScore');
    const result = await this._mpl.getLastContentQualityEvaluation(current.contentId);
    const score = result.responses.length > 0 ? result.score : undefined;
    this._opened = { ...current, evaluationScore: score };
    this._emitter.emit('changed');
  }

  /**
   * @throws {ContentValidationError} when a draft save fails pre-save validation.
   * @throws {SaveWorkingCopyError} when the intermediate working-copy flush fails.
   * @throws {SaveDraftError} when any post-flush step fails: committing the
   *   audience, content, or inline-comment offsets to the server, or recycling
   *   the working copy afterwards.
   */
  private async doSave(opts: { readonly isUnlock?: boolean }): Promise<void> {
    let current = this.requireOpen('save');
    this.requireEditable(current.options, 'save');

    // Push each editor's debounced keystroke into staging so the flush below
    // captures the freshest edit. Every save path funnels through here, so
    // callers don't need to remember to drain.
    this._flushCoordinator.drainFlushers();

    // Inline the auto-saving flush so it doesn't re-queue on that
    // service's queue while we're waiting on our own queue.
    const flushed = await this._autoSaving.flush();

    // Save even when nothing was staged: saving marks content as MODIFIED,
    // which downstream workflow steps (bar-raiser review, submission) require.
    let contentToSave = flushed.content ?? current.document.content;

    // Gate the draft save exactly like the legacy editor's beforeSaving:
    // validate metadata/template/links and abort before any lock or server
    // write when anything is invalid. On success we persist the normalized
    // copy the validator returns (links rewritten), not the live working copy.
    if (current.options.kind === 'edit-draft') {
      contentToSave = this.validateBeforeSave(contentToSave);
    }

    // Acquire the edit lock before committing draft content.
    if (current.options.kind === 'edit-draft') {
      try {
        // Carry the new lock forward so the handle rebuilt after the write keeps it.
        current = await this.doAcquireLockIfNeeded(current);
      } catch (err: unknown) {
        if (err instanceof UserNotLoggedInError) {
          // A known precondition failure (not a backend call) — wrap it directly.
          throw new SaveDraftError('Cannot save: no user is logged in.', err);
        }
        throw err;
      }
    }

    const isAudienceChanged =
      current.options.kind === 'edit-draft' &&
      flushed.audience !== undefined &&
      flushed.audience !== current.document.contentAudience;
    if (isAudienceChanged) {
      await this._mpl
        .updateWorkflowContentAudience(current.contentId, flushed.audience!)
        .catch(
          handleMplClientError(
            'Failed to update the audience on the server. Please wait a few seconds and retry.',
          ),
        );
    }
    if (current.options.kind === 'edit-draft') {
      await this._mpl
        .updateContent(current.contentId, contentToSave, opts.isUnlock ?? false)
        .catch(
          handleMplClientError(
            'Failed to commit the draft content to the server. Please wait a few seconds and retry.',
          ),
        );
    } else if (current.options.kind === 'edit-finalizing') {
      await this._mpl
        .updateFinalizingContent(current.contentId, current.options.workflowId, contentToSave)
        .catch(
          handleMplClientError(
            'Failed to commit the finalizing content to the server. Please wait a few seconds and retry.',
          ),
        );
    }
    // Advance the open handle to reflect what we just persisted. Without
    // this, once the working copy is discarded below and reopened fresh,
    // the content/audience getters would fall back to the original server
    // snapshot. The rest of `document` (workflow, timestamps, etc.) is
    // NOT reloaded — callers that need refreshed workflow state should
    // call a dedicated refresh method (to be added when needed).
    this._opened = {
      ...current,
      document: {
        ...current.document,
        content: contentToSave,
        contentAudience: isAudienceChanged ? flushed.audience : current.document.contentAudience,
      },
    };

    this.recalculateNodeIdMappings(contentToSave);

    // Recycle the working copy so subsequent edits start from a clean slate.
    await this._autoSaving
      .discard()
      .catch(
        handleMplClientError(
          'Draft was committed, but cleaning up the working copy afterwards failed.',
        ),
      );
    if (isEditable(current.options)) {
      await this._autoSaving
        .open(current.contentId, {
          workingCopyKey: workingCopyKeyFor(current.options),
          isRestore: false,
        })
        .catch(
          handleMplClientError(
            'Draft was committed, but reopening the working copy afterwards failed.',
          ),
        );
    }
    this._emitter.emit('changed');
    this.scheduleEventsFetch();
  }

  /**
   * @throws {ContentValidationError} when a draft save fails pre-save validation.
   * @throws {SaveWorkingCopyError} when the intermediate working-copy flush fails.
   * @throws {SaveDraftError} when committing to the server or recycling the
   *   working copy afterwards fails.
   */
  private async doCloseAndSave(opts: { readonly isUnlock?: boolean }): Promise<void> {
    // Can't call save() directly — it would deadlock on the same queue.
    await this.doSave(opts);
    try {
      await this._autoSaving.discard();
    } catch (err) {
      // Best-effort cleanup after a successful save — the user's work is
      // already safely on the server, so don't fail the close.
      log.warn({ err }, 'Working-copy cleanup after save failed');
    }
    this.teardown();
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async doCloseAndDiscard(opts: { readonly isUnlock?: boolean }): Promise<void> {
    const current = this._opened;
    if (!current) return;

    // Release the edit lock before tearing down, while the handle (and its
    // workflow/lock state) is still available. Best-effort: the local edits
    // are already being discarded, so a failed unlock must not trap the user
    // in the editor.
    if (opts.isUnlock) {
      await this.releaseLockIfHeld(current);
    }

    try {
      this.teardown();
    } finally {
      if (isEditable(current.options)) {
        await this._autoSaving.discard();
      }
    }
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async doRefreshWorkflow(): Promise<void> {
    if (!this._opened) return;
    const doc = this._opened.document;
    if (doc.kind !== 'draft' && doc.kind !== 'finalizing') return;

    const { workflow } = await this._mpl.getContentWorkflow(this._opened.contentId);
    this._opened = {
      ...this._opened,
      document: { ...doc, workflow },
      attributions: undefined,
    };
    this._emitter.emit('changed');
    this.scheduleAttributionsFetch();
  }

  private scheduleEventsFetch(): void {
    const opened = this._opened;
    if (!opened) return;
    const contentId = opened.contentId;
    void this.fetchEvents(opened).then(
      (loaded) => {
        if (!loaded) return;
        // Guard: the content may have been closed while we were fetching.
        if (this._opened?.contentId !== contentId) return;
        this._opened = { ...this._opened, ...loaded };
        this._emitter.emit('changed');
      },
      (err: unknown) => {
        log.error({ err, contentId }, 'Failed to load workflow events');
      },
    );
  }

  private scheduleAttributionsFetch(): void {
    const current = this._opened;
    if (!current) return;

    const requestId = ++this._attributionsRequestId;
    const { document, contentId } = current;
    const workflow =
      document.kind === 'draft' || document.kind === 'finalizing' ? document.workflow : undefined;

    const loadAssignedUser = async (
      userId: string | undefined,
      role: string,
    ): Promise<ContentAttributionUser | undefined> => {
      if (!userId) return undefined;
      try {
        const profile = await this._mpl.getUserProfile(userId);
        return { userId, userName: profile.userName };
      } catch (err) {
        log.warn({ err, contentId, userId, role }, 'Failed to load content attribution profile');
        return { userId };
      }
    };

    void Promise.all([
      loadAssignedUser(workflow?.barRaiserId, 'content bar raiser'),
      loadAssignedUser(workflow?.techWriterId, 'technical writer'),
    ]).then(([contentBarRaiser, technicalWriter]) => {
      if (requestId !== this._attributionsRequestId || !this._opened) return;

      this._opened = {
        ...this._opened,
        attributions: { contentBarRaiser, technicalWriter },
      };
      this._emitter.emit('changed');
    });
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async doRefreshEvents(): Promise<void> {
    const opened = this._opened;
    if (!opened) return;
    const loaded = await this.fetchEvents(opened);
    if (!loaded) return;

    if (this._opened?.contentId !== opened.contentId) return;
    this._opened = { ...this._opened, ...loaded };
    this._emitter.emit('changed');
  }

  /**
   * Loads {@link LoadedDocumentHandle.events} and
   * {@link LoadedDocumentHandle.hasUnpublishedEvents}. Returns undefined when
   * the open document's workflow can't be resolved.
   *
   * @throws {ServiceCallError} when fetching the current version's events fails.
   */
  private async fetchEvents(
    opened: LoadedDocumentHandle,
  ): Promise<
    | { readonly events: readonly WorkflowEvent[]; readonly hasUnpublishedEvents: boolean }
    | undefined
  > {
    const workflowId = await this.resolveWorkflowId(opened);
    if (workflowId === undefined) return undefined;

    // Load the current version's and the unpublished publications' events together.
    const [{ events }, unpublishedEvents] = await Promise.all([
      this._mpl.getContentEvents(opened.contentId, workflowId),
      this.fetchUnpublishedPublicationEvents(opened),
    ]);
    return {
      events: [...events, ...unpublishedEvents],
      hasUnpublishedEvents: unpublishedEvents.length > 0,
    };
  }

  /**
   * Loads the events of a draft's unpublished publications, so the activity of
   * content that was taken down stays visible. Empty for non-drafts, when
   * nothing was unpublished, or when the user may not see the activity.
   */
  private async fetchUnpublishedPublicationEvents(
    opened: LoadedDocumentHandle,
  ): Promise<readonly WorkflowEvent[]> {
    const { contentId, document } = opened;
    if (document.kind !== 'draft') return [];

    // Past publications' activity is limited to the content's authors and staff roles.
    const coAuthorIds = document.coAuthors.map((coAuthor) => coAuthor.userId);
    if (!this._userService.canSeeActivityForContent(document.author.userId, coAuthorIds)) {
      return [];
    }

    // Unpublishing marks every live publication UNPUBLISHED; each keeps its own events.
    const workflowIds = (document.workflow.publicationWorkflows ?? [])
      .filter((wf) => wf.state === WorkflowState.UNPUBLISHED)
      .map((wf) => wf.workflowId);
    if (workflowIds.length === 0) return [];

    // A publication whose events fail to load drops only its own; the rest still show.
    const results = await Promise.allSettled(
      workflowIds.map((id) => this._mpl.getContentEvents(contentId, id)),
    );
    return results.flatMap((result, i) => {
      if (result.status === 'fulfilled') return result.value.events;
      log.warn(
        { err: result.reason, contentId, workflowId: workflowIds[i] },
        'Failed to load unpublished publication events',
      );
      return [];
    });
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async resolveWorkflowId(opened: LoadedDocumentHandle): Promise<number | undefined> {
    const { document, options } = opened;
    if (document.kind !== 'publication') return document.workflow.draftWorkflowId;

    const knownWorkflowId =
      (options.kind === 'read-publication' ? options.workflowId : undefined) ?? document.workflowId;
    if (knownWorkflowId !== undefined) return knownWorkflowId;

    const { workflows } = await this._mpl.listWorkflowHistory(opened.contentId);
    const matchingAudience = workflows.filter(
      (workflow) =>
        isPublicationViewable(workflow.state) &&
        workflow.contentAudience === document.contentAudience,
    );
    const exactPublication = matchingAudience.find(
      (workflow) => workflow.publicationDate.toMillis() === document.publicationDate.toMillis(),
    );
    if (exactPublication) return exactPublication.workflowId;

    return [...matchingAudience].sort(
      (a, b) => b.publicationDate.toMillis() - a.publicationDate.toMillis(),
    )[0]?.workflowId;
  }

  /**
   * Takes the lock when needed and keeps it on the open handle; returns that handle.
   *
   * @throws {SaveDraftError} when acquiring or confirming the edit lock fails.
   * @throws {UserNotLoggedInError}
   */
  private async doAcquireLockIfNeeded(handle: LoadedDocumentHandle): Promise<LoadedDocumentHandle> {
    const doc = handle.document;
    if (doc.kind !== 'draft') return handle;

    const { workflow } = doc;
    const lock = workflow.lock;

    // Already locked by the current user — nothing to do.
    if (lock && lock.acquiredBy === this._userService.userId) return handle;

    // Locked by the system or someone else — cannot proceed.
    if (lock) {
      throw new SaveDraftError(describeLockHeldByOther(lock.lockType), undefined);
    }

    // Not locked — acquire via LOCK workflow event.
    await this._mpl
      .postWorkflowEvent(handle.contentId, workflow.draftWorkflowId, WorkflowEventTypes.LOCK, '')
      .catch(
        handleMplClientError(
          'Failed to acquire the edit lock. Please wait a few seconds and retry.',
        ),
      );

    // The LOCK event is applied asynchronously — wait until the workflow reports
    // the lock held by us, so callers don't mutate content before it takes hold.
    const lockedWorkflow = await this.waitForLockHeld(handle.contentId);

    // Keep the new lock right away, even if a later write fails, so the next caller doesn't post a second LOCK.
    // Start from the latest handle so events or names that loaded during the wait are kept.
    const locked = { ...(this._opened ?? handle), document: { ...doc, workflow: lockedWorkflow } };
    this._opened = locked;
    this._emitter.emit('changed');
    return locked;
  }

  /**
   * Polls the workflow until the edit lock is held by the current user.
   *
   * @throws {SaveDraftError} when another user holds the lock, when polling the
   *   workflow fails, or when the lock does not appear within the poll window.
   */
  private async waitForLockHeld(contentId: string): Promise<ContentWorkflow> {
    const userId = this._userService.userId;

    // Snapshot the start so the loop stops once the budget has elapsed,
    // regardless of how long each getContentWorkflow round-trip takes.
    const deadline = Date.now() + LOCK_POLL_TIMEOUT_MS;

    while (true) {
      const { workflow } = await this._mpl
        .getContentWorkflow(contentId)
        .catch(
          handleMplClientError(
            'Failed to confirm the edit lock. Please wait a few seconds and retry.',
          ),
        );

      // Lock landed under our user — safe to proceed.
      if (workflow.lock?.acquiredBy === userId) return workflow;

      // The system or someone else won the lock while we were waiting — cannot proceed.
      if (workflow.lock) {
        throw new SaveDraftError(describeLockHeldByOther(workflow.lock.lockType), undefined);
      }

      // Lock not applied yet — give up once the budget is spent, else back off.
      if (Date.now() >= deadline) {
        throw new SaveDraftError(
          'Timed out waiting for the edit lock. Please wait a few seconds and retry.',
          undefined,
        );
      }
      await promiseTimeout(LOCK_POLL_INTERVAL_MS);
    }
  }

  private async releaseLockIfHeld(handle: LoadedDocumentHandle): Promise<void> {
    const doc = handle.document;
    if (doc.kind !== 'draft') return;

    // Only release a lock we actually hold — never touch someone else's lock
    // or a system lock, and skip when nothing is locked.
    const lock = doc.workflow.lock;
    if (!lock || lock.acquiredBy !== this._userService.userId) return;

    // Best-effort: log and swallow so discarding never fails on the unlock.
    // Uses the same DELETE /lock endpoint as the legacy editor — the backend
    // does not accept an UNLOCK workflow event.
    await this._mpl.unlockWorkflow(handle.contentId).catch((err: unknown) => {
      log.warn({ err, contentId: handle.contentId }, 'Failed to release the edit lock on discard');
    });
  }

  private requireOpen(method: string): LoadedDocumentHandle {
    if (!this._opened) {
      throw new Error(`Cannot call ${method}: no content is open. Call open() first.`);
    }
    return this._opened;
  }

  private requireEditable(options: ContentLoadOptions, method: string): void {
    if (!isEditable(options)) {
      throw new Error(
        `Cannot call ${method}: content was opened with kind "${options.kind}" which is read-only.`,
      );
    }
  }

  /**
   * Validate a draft before saving and return the normalized copy to persist,
   * mirroring the legacy editor's beforeSaving gate.
   *
   * @throws {ContentValidationError} when the content is invalid.
   */
  private validateBeforeSave(content: Content): Content {
    // The validator rewrites links in place (trim href, force rel, http→https),
    // so run it on a clone: a blocked save leaves the live working copy
    // untouched, and a passing save persists the normalized clone.
    const candidate = deepCloneContent(content);
    const result = this._contentValidator(
      candidate,
      new Set([ContentSchemaSection.ContentMetadata, ContentSchemaSection.TemplateMetadata]),
      this._validationEntities,
    );
    if (result.isValid) return candidate;

    // Template errors take precedence over metadata errors on the same key (later Map entry wins);
    // link errors are already {key,value} pairs and append after.
    const issues: ContentValidationIssue[] = Array.from(
      new Map([...result.contentMetadataError, ...result.templateValidationError]),
    ).map(([key, value]) => ({ key, value }));
    issues.push(...result.contentDataLinkValidationError);
    throw new ContentValidationError(issues);
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async mutateCoAuthors(
    method: string,
    persist: (contentId: string) => Promise<unknown>,
    transform: (coAuthors: readonly ContentAccessUser[]) => readonly ContentAccessUser[],
  ): Promise<void> {
    const handle = this.requireOpen(method);
    this.requireEditable(handle.options, method);

    await persist(handle.contentId);

    this._opened = {
      ...handle,
      document: {
        ...handle.document,
        coAuthors: transform(handle.document.coAuthors),
      },
    };
    this._emitter.emit('changed');
  }

  private recalculateNodeIdMappings(content: Content): void {
    this._nodeIdMappings = content ? getNodeIdMappings(content) : undefined;
  }

  private teardown(): void {
    if (this._unsubscribeAutoSaving) {
      this._unsubscribeAutoSaving();
      this._unsubscribeAutoSaving = undefined;
    }
    this._attributionsRequestId++;
    this._opened = undefined;
    this._nodeIdMappings = undefined;
    this._emitter.emit('changed');
  }

  /**
   * @throws {ServiceCallError} when the backend call fails.
   */
  private async fetchDocument(
    contentId: string,
    options: ContentLoadOptions,
  ): Promise<LoadedDocument> {
    // Published content: latest version when no workflowId, else a specific
    // historical version by workflowId.
    if (options.kind === 'read-publication') {
      const [publication, { accessUsers }] = await Promise.all([
        options.workflowId !== undefined
          ? this._mpl.getPublicationByWorkflow(contentId, options.workflowId)
          : this._mpl.getPublication(contentId),
        this._mpl.listContentSharedWith(contentId),
      ]);
      return publicationToLoadedDocument(contentId, publication, accessUsers);
    }
    // Finalizing modes load the publication AND the owning draft workflow in
    // parallel — downstream logic needs workflow fields (draftWorkflowId,
    // publicationState, isLocked, etc.) that aren't on the publication.
    if (options.kind === 'read-finalizing' || options.kind === 'edit-finalizing') {
      const classifier =
        options.kind === 'read-finalizing' && options.userId !== undefined
          ? `finalizing/${options.userId}`
          : 'finalizing';
      const [publication, { workflow }, { accessUsers }] = await Promise.all([
        this._mpl.getPublicationByWorkflow(contentId, options.workflowId, classifier),
        this._mpl.getContentWorkflow(contentId),
        this._mpl.listContentSharedWith(contentId),
      ]);
      return finalizingToLoadedDocument(contentId, publication, workflow, accessUsers);
    }
    // Draft modes (read-draft / edit-draft): load from the draft endpoint.
    const [result, { accessUsers }] = await Promise.all([
      this._mpl.getContent(contentId),
      this._mpl.listContentSharedWith(contentId),
    ]);
    return draftToLoadedDocument(
      contentId,
      result.content,
      result.createdAt as DateTime,
      result.workflow,
      accessUsers,
    );
  }
}
